/**
 * ImmobilienDetail – Detailseite für ein einzelnes Objekt
 *
 * URL-Schema: /immobilien/[ID]-[slug]
 * Die ID (Spalte A) ist der stabile Anker; der Slug-Teil kann sich ändern.
 *
 * Diese Seite ist die Besucher-Ansicht (React-SPA).
 * Crawler erhalten das serverseitig gerenderte HTML aus immobilien-ssr.ts.
 * Beide Ansichten zeigen denselben Inhalt → kein Cloaking.
 */

import { useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, MapPin, BedDouble, Maximize2, Home, Calendar, Phone, ExternalLink, Video, Loader2, AlertCircle, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SEO from "@/components/SEO";
import { useImmobilien } from "@/pages/Immobilien";
import { stripPropertyMarkdownMeta } from "@shared/property-markdown";
import PropertyLocationMap from "@/components/PropertyLocationMap";
import { useLocale } from "@/contexts/LocaleContext";

// ─── Slug-Hilfsfunktionen (gespiegelt vom Server) ────────────────────────────
function toSlugPart(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildImmobilienSlug(id: string, ort: string, typ: string): string {
  return `${id}-${toSlugPart(ort)}-${toSlugPart(typ)}`;
}

function extractIdFromSlug(slug: string): string {
  const match = slug.match(/^([A-Za-z0-9]+)/);
  return match ? match[1] : slug;
}

function formatPreis(preis: string): string {
  const num = parseInt(preis.replace(/\D/g, ""), 10);
  if (isNaN(num)) return "Preis auf Anfrage";
  return num.toLocaleString("de-DE") + " € VHB";
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function ImmobilienDetail() {
  const [, params] = useRoute("/immobilien/:slug");
  const slug = params?.slug ?? "";
  const id = extractIdFromSlug(slug);

  const { data: immobilien, loading, error } = useImmobilien();
  const { t } = useLocale();
  const obj = immobilien.find((o) => o.id === id) ?? null;
  const [markdown, setMarkdown] = useState("");
  const [markdownLoading, setMarkdownLoading] = useState(false);

  useEffect(() => {
    const markdownUrl = obj?.markdown_url;
    if (!markdownUrl) {
      setMarkdown("");
      setMarkdownLoading(false);
      return;
    }

    let active = true;
    setMarkdownLoading(true);
    fetch(markdownUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Status ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (active) setMarkdown(stripPropertyMarkdownMeta(text));
      })
      .catch(() => {
        // Spalte L bleibt der sichere Fallback, falls ein Drive-Link vorübergehend nicht erreichbar ist.
        if (active) setMarkdown("");
      })
      .finally(() => {
        if (active) setMarkdownLoading(false);
      });

    return () => {
      active = false;
    };
  }, [obj?.markdown_url]);

  const isVerkauft = obj?.status === "Verkauft" || obj?.online_status === "Verkauft";
  const ortPlz = obj ? (obj.plz && obj.ort ? `${obj.plz} ${obj.ort}` : obj.ort) : "";

  // OG-Bild-URL für SEO-Komponente
  const driveId = obj?.bild_url
    ? (() => {
        const m1 = obj.bild_url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
        if (m1) return m1[1];
        const m2 = obj.bild_url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
        if (m2) return m2[1];
        return null;
      })()
    : null;
  const ogImage = driveId ? `/api/og-image?id=${driveId}` : undefined;

  // ── Ladestate ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#a066c2]" size={40} />
      </div>
    );
  }

  if (error || (!loading && !obj)) {
    return (
      <div className="min-h-screen bg-[#faf8f5]">
        <SiteHeader />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <AlertCircle size={48} className="text-[#a066c2] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#2d1b4e] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Objekt nicht mehr verfügbar
          </h1>
          <p className="text-[#6b5b7e] mb-8">
            Dieses Objekt wurde bereits verkauft oder ist nicht mehr auf dem Markt.
          </p>
          <Link href="/immobilien"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:scale-105"
            style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
            <ArrowLeft size={16} /> Alle Immobilien ansehen
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://immozwerg.com/immobilien/${buildImmobilienSlug(obj!.id, obj!.ort, obj!.typ)}`;
  const seoDescription = obj!.beschreibung
    ? obj!.beschreibung.slice(0, 160)
    : `${obj!.typ} in ${ortPlz} – Immozwerg, Andrea Heinz`;

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SEO
        title={obj!.objekttitel || `${obj!.typ} in ${ortPlz}`}
        description={seoDescription}
        canonical={canonicalUrl}
        ogImage={ogImage}
        ogType="website"
        keywords={`Immobilien ${obj!.ort}, ${obj!.typ} kaufen, Immozwerg`}
        noIndex={isVerkauft}
      />
      <SiteHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-[#a066c2] mb-6">
          <Link href="/" className="hover:underline">Startseite</Link>
          <span className="text-[#c9b8d8]">/</span>
          <Link href="/immobilien" className="hover:underline">Immobilien</Link>
          <span className="text-[#c9b8d8]">/</span>
          <span className="text-[#6b5b7e] truncate max-w-[200px]">{obj!.objekttitel || obj!.ort}</span>
        </nav>

        {/* Hauptbild */}
        <div className="relative rounded-2xl overflow-hidden shadow-md mb-8 aspect-video bg-[#e8dff0]">
          <img
            src={obj!.bild_url || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80"}
            alt={obj!.objekttitel || obj!.ort}
            data-original-content
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80";
            }}
          />
          <div className="absolute top-4 left-4 rounded-lg bg-[#2d1b4e]/85 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm">
            {t("objectNumber")} {obj!.id}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Linke Spalte: Details */}
          <div className="md:col-span-2">
            {/* Status-Badge */}
            {isVerkauft && (
              <div className="inline-flex items-center gap-2 bg-[#f0eaf8] text-[#631172] text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                🏠 Neue Eigentümer gefunden
              </div>
            )}

            <h1 className="text-2xl sm:text-3xl font-bold text-[#2d1b4e] mb-2 leading-snug"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              <span data-original-content>{obj!.objekttitel || `${obj!.typ} in ${ortPlz}`}</span>
            </h1>

            <div className="flex items-center gap-1.5 text-[#a066c2] text-sm mb-6">
              <MapPin size={14} />
              <span data-original-content>{ortPlz}</span>
            </div>

            {/* Kennzahlen */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              {obj!.zimmer && (
                <div className="bg-white rounded-xl p-3 shadow-sm border border-[#e8dff0] text-center">
                  <BedDouble size={18} className="text-[#a066c2] mx-auto mb-1" />
                  <div className="font-bold text-[#2d1b4e] text-lg">{obj!.zimmer}</div>
                  <div className="text-xs text-[#6b5b7e]">Zimmer</div>
                </div>
              )}
              {obj!.wohnflaeche && (
                <div className="bg-white rounded-xl p-3 shadow-sm border border-[#e8dff0] text-center">
                  <Maximize2 size={18} className="text-[#a066c2] mx-auto mb-1" />
                  <div className="font-bold text-[#2d1b4e] text-lg">{obj!.wohnflaeche}</div>
                  <div className="text-xs text-[#6b5b7e]">m² Wohnfläche</div>
                </div>
              )}
              {obj!.grundstueck && (
                <div className="bg-white rounded-xl p-3 shadow-sm border border-[#e8dff0] text-center">
                  <Home size={18} className="text-[#a066c2] mx-auto mb-1" />
                  <div className="font-bold text-[#2d1b4e] text-lg">{obj!.grundstueck}</div>
                  <div className="text-xs text-[#6b5b7e]">m² Grundstück</div>
                </div>
              )}
              {obj!.baujahr && (
                <div className="bg-white rounded-xl p-3 shadow-sm border border-[#e8dff0] text-center">
                  <Calendar size={18} className="text-[#a066c2] mx-auto mb-1" />
                  <div className="font-bold text-[#2d1b4e] text-lg">{obj!.baujahr}</div>
                  <div className="text-xs text-[#6b5b7e]">Baujahr</div>
                </div>
              )}
            </div>

            {/* Beschreibung */}
            {(obj!.beschreibung || markdownLoading || markdown) && (
              <div className="mb-8">
                <h2 className="text-lg font-bold text-[#2d1b4e] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Objektbeschreibung
                </h2>
                {markdownLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[#6b5b7e]" aria-live="polite">
                    <Loader2 size={16} className="animate-spin text-[#a066c2]" />
                    Ausführliche Objektbeschreibung wird geladen …
                  </div>
                ) : markdown ? (
                  <div className="prose prose-sm max-w-none text-[#6b5b7e] prose-headings:text-[#2d1b4e] prose-a:text-[#631172] prose-a:font-semibold" data-original-content>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-[#6b5b7e] leading-relaxed" data-original-content>{obj!.beschreibung}</p>
                )}
              </div>
            )}

            {obj!.plz && obj!.ort && <PropertyLocationMap postalCode={obj!.plz} city={obj!.ort} />}

            {/* Energieklasse */}
            {obj!.energieklasse && obj!.energieklasse !== "liegt vor" && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-[#e8dff0] mb-6 inline-flex items-center gap-3">
                <span className="text-sm text-[#6b5b7e]">Energieklasse:</span>
                <span className="font-bold text-[#2d1b4e] text-lg">{obj!.energieklasse}</span>
              </div>
            )}
          </div>

          {/* Rechte Spalte: Preis + CTAs */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-[#e8dff0] p-6 sticky top-6">
              {isVerkauft ? (
                <div className="text-center py-4">
                  <div className="text-2xl mb-2">🏠</div>
                  <div className="font-bold text-[#631172] text-lg mb-1">Verkauft</div>
                  <p className="text-sm text-[#6b5b7e]">Neue Eigentümer gefunden</p>
                </div>
              ) : (
                <>
                  <div className="text-xs text-[#a066c2] font-medium mb-1">Kaufpreis</div>
                  <div className="font-bold text-[#2d1b4e] text-2xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {obj!.preis ? formatPreis(obj!.preis) : "Preis auf Anfrage"}
                  </div>

                  {/* Exposé-Link */}
                  {obj!.link && (
                    <a href={obj!.link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-sm font-bold text-white mb-3 transition-all hover:scale-105"
                      style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}>
                      Vollständiges Exposé <ExternalLink size={14} />
                    </a>
                  )}

                  {/* 360°-Rundgang */}
                  {obj!.rundgang_url && (
                    <a href={obj!.rundgang_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-sm font-bold mb-3 transition-all hover:scale-105 border-2"
                      style={{ borderColor: "#a066c2", color: "#631172" }}>
                      <Video size={14} /> 360°-Rundgang
                    </a>
                  )}

                  {/* Kontakt */}
                  <Link href="/kontakt"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 border-2"
                    style={{ borderColor: "#e8dff0", color: "#6b5b7e" }}>
                    <Phone size={14} /> Anfrage stellen
                  </Link>

                  {/* Teilen-Button */}
                  <button
                    onClick={() => {
                      const url = canonicalUrl;
                      if (navigator.share) {
                        navigator.share({ title: obj!.objekttitel || `${obj!.typ} in ${ortPlz}`, url });
                      } else {
                        navigator.clipboard?.writeText(url).then(() => alert("Link kopiert!"));
                      }
                    }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full text-sm font-bold mt-3 transition-all hover:scale-105 border-2"
                    style={{ borderColor: "#e8dff0", color: "#6b5b7e" }}>
                    <Share2 size={14} /> Teilen
                  </button>
                </>
              )}

              <div className="mt-6 pt-4 border-t border-[#f0eaf8]">
                <Link href="/immobilien"
                  className="flex items-center gap-2 text-sm text-[#a066c2] hover:underline">
                  <ArrowLeft size={14} /> Alle Objekte
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
